import type { CaptionCard, Segment, Word } from "./clip.js";

export interface SegmentOptions { gapMs?: number; padMs?: number }

/** Silence cut list: keep ranges of the source between startMs and endMs, dropping gaps between words longer than gapMs. */
export function buildSegments(words: Word[], startMs: number, endMs: number, opts: SegmentOptions = {}): Segment[] {
  const gapMs = opts.gapMs ?? 1000;
  const padMs = opts.padMs ?? 200;
  const inRange = words.filter((w) => w.endMs > startMs && w.startMs < endMs);
  if (inRange.length === 0) return [{ inMs: startMs, outMs: endMs }];
  const segments: Segment[] = [];
  let segIn = startMs;
  for (let i = 1; i < inRange.length; i++) {
    const prev = inRange[i - 1];
    const next = inRange[i];
    const gap = next.startMs - prev.endMs;
    if (gap > gapMs) {
      segments.push({ inMs: segIn, outMs: Math.min(prev.endMs + padMs, endMs) });
      segIn = Math.max(next.startMs - padMs, startMs);
    }
  }
  segments.push({ inMs: segIn, outMs: endMs });
  return segments.filter((s) => s.outMs > s.inMs);
}

/** Splits segments at detected silences longer than gapMs (keeping padMs on both sides). Word-based cuts stay; audio silences add cuts the words missed. */
export function cutSilences(segments: Segment[], silences: { startMs: number; endMs: number }[], opts: SegmentOptions = {}): Segment[] {
  const gapMs = opts.gapMs ?? 1000;
  const padMs = opts.padMs ?? 200;
  const out: Segment[] = [];
  for (const seg of segments) {
    let cursor = seg.inMs;
    const inside = silences
      .filter((s) => s.endMs - s.startMs > gapMs && s.startMs < seg.outMs && s.endMs > seg.inMs)
      .sort((a, b) => a.startMs - b.startMs);
    for (const sil of inside) {
      const cutIn = Math.max(seg.inMs, sil.startMs + padMs);
      const cutOut = Math.min(seg.outMs, sil.endMs - padMs);
      if (cutOut - cutIn <= gapMs - 2 * padMs) continue; // too short once padded
      if (cutIn > cursor) out.push({ inMs: cursor, outMs: cutIn });
      cursor = cutOut;
    }
    if (seg.outMs > cursor) out.push({ inMs: cursor, outMs: seg.outMs });
  }
  return out.filter((s) => s.outMs - s.inMs >= 200);
}

/** Playback (timeline) time of a source time, or null if the source time falls inside a cut. */
export function sourceToTimeline(segments: Segment[], sourceMs: number): number | null {
  let acc = 0;
  for (const s of segments) {
    if (sourceMs >= s.inMs && sourceMs <= s.outMs) return acc + (sourceMs - s.inMs);
    acc += s.outMs - s.inMs;
  }
  return null;
}

/** Source time of a timeline time (clamped to the last segment). */
export function timelineToSource(segments: Segment[], timelineMs: number): number {
  let acc = 0;
  for (const s of segments) {
    const len = s.outMs - s.inMs;
    if (timelineMs < acc + len) return s.inMs + (timelineMs - acc);
    acc += len;
  }
  const last = segments[segments.length - 1];
  return last ? last.outMs : 0;
}

/** The piece a timeline time falls in: its index, its source range, and where it starts on the timeline (the last piece past the end). */
export function pieceAt(segments: Segment[], timelineMs: number): { index: number; inMs: number; outMs: number; startMs: number } | null {
  let acc = 0;
  for (let i = 0; i < segments.length; i++) {
    const s = segments[i];
    const len = s.outMs - s.inMs;
    if (timelineMs < acc + len || i === segments.length - 1) return { index: i, inMs: s.inMs, outMs: s.outMs, startMs: acc };
    acc += len;
  }
  return null;
}

export function timelineDuration(segments: Segment[]): number {
  return segments.reduce((a, s) => a + (s.outMs - s.inMs), 0);
}

/** Nearest sensible cut point: the middle of a silence inside ±windowMs, else the nearest word boundary, else ms itself. */
export function snapToBoundary(
  words: Word[],
  ms: number,
  silences: { startMs: number; endMs: number }[],
  windowMs = 1500,
): number {
  const near = silences
    .map((s) => ({ mid: (s.startMs + s.endMs) / 2, dist: Math.abs((s.startMs + s.endMs) / 2 - ms) }))
    .filter((s) => s.dist <= windowMs)
    .sort((a, b) => a.dist - b.dist);
  if (near.length) return near[0].mid;
  const bounds = words.flatMap((w) => [w.startMs, w.endMs]).filter((b) => Math.abs(b - ms) <= windowMs);
  if (!bounds.length) return ms;
  return bounds.reduce((best, b) => (Math.abs(b - ms) < Math.abs(best - ms) ? b : best));
}

export interface PageOptions { min?: number; max?: number; maxChars?: number; pauseMs?: number; breaksMs?: number[] }
const REF_RE = /^\[\d+:\d+(?:-\d+)?\]$/;
const END_PUNCT = /[.!?؟۔]["'”’)»]*$/;
const SOFT_PUNCT = /[:;,،]["'”’)»]*$/;
/** Words that should not be left dangling at the end of a card. */
const DANGLING = new Set(["the", "a", "an", "of", "to", "and", "or", "for", "in", "on", "at", "by", "with", "from", "is", "are", "was", "were", "be", "that", "this", "his", "her", "their", "our", "your", "my", "its", "as", "but", "so", "if", "than", "into", "about", "not", "no"]);
const bare = (t: string) => t.toLowerCase().replace(/[^\p{L}\p{N}']/gu, "");

/** Groups timeline words into karaoke cards: 3–max words, breaks on end punctuation, soft punctuation, long pauses and a char budget; never orphans a [s:a] ref or ends on a dangling function word when avoidable. */
export function pageCaptions(words: Word[], opts: PageOptions = {}): CaptionCard[] {
  const min = opts.min ?? 3;
  const max = opts.max ?? 6;
  const maxChars = opts.maxChars ?? 28;
  const pauseMs = opts.pauseMs ?? 900;
  const breaks = new Set(opts.breaksMs ?? []);
  const cards: CaptionCard[] = [];
  let cur: Word[] = [];
  const flush = () => {
    if (!cur.length) return;
    const prev = cards[cards.length - 1];
    // an orphan (fewer than min words) joins the previous card when it fits and follows without a pause
    if (prev && cur.length < min && prev.words.length + cur.length <= max + 1 && cur[0].startMs - prev.endMs < pauseMs) {
      prev.words = [...prev.words, ...cur];
      prev.endMs = cur[cur.length - 1].endMs;
      cur = [];
      return;
    }
    cards.push({ id: `c${cards.length + 1}`, startMs: cur[0].startMs, endMs: cur[cur.length - 1].endMs, words: cur });
    cur = [];
  };
  /** flush, but hand a trailing dangling word (or a word before a ref) to the next card when the card can spare it */
  const flushSmart = () => {
    const carry: Word[] = [];
    while (cur.length > min && carry.length < 2) {
      const last = cur[cur.length - 1];
      if (!DANGLING.has(bare(last.text)) || END_PUNCT.test(last.text)) break;
      carry.unshift(last);
      cur = cur.slice(0, -1);
    }
    flush();
    cur = carry;
  };
  for (let i = 0; i < words.length; i++) {
    const w = words[i];
    const next = words[i + 1];
    const chars = cur.reduce((a, x) => a + x.text.length + 1, 0) + w.text.length;
    const nextIsRef = next ? REF_RE.test(next.text) : false;
    if (cur.length && breaks.has(w.startMs)) flush();
    else if (cur.length && (cur.length >= max || (chars > maxChars && cur.length >= min && !REF_RE.test(w.text)))) flushSmart();
    cur.push(w);
    const pause = next ? next.startMs - w.endMs : 0;
    const endsSentence = END_PUNCT.test(w.text) && !nextIsRef;
    const softBreak = SOFT_PUNCT.test(w.text) && cur.length >= min && !nextIsRef;
    if (endsSentence || pause > pauseMs) flush();
    else if (softBreak) flush();
    else if (cur.length >= max && !nextIsRef) flushSmart();
  }
  flush();
  return cards;
}

export interface DuckOptions { duckDb: number; attackMs?: number; releaseMs?: number; bridgeMs?: number }

/** Volume envelope (dB offsets) for a music bed: ducked while speech is present. Points are step changes. */
export function duckEnvelope(words: Word[], opts: DuckOptions): { ms: number; db: number }[] {
  const attack = opts.attackMs ?? 120;
  const release = opts.releaseMs ?? 400;
  const bridge = opts.bridgeMs ?? 350;
  const points: { ms: number; db: number }[] = [{ ms: 0, db: 0 }];
  if (!words.length) return points;
  const runs: { s: number; e: number }[] = [];
  for (const w of words) {
    const last = runs[runs.length - 1];
    if (last && w.startMs - last.e <= bridge) last.e = Math.max(last.e, w.endMs);
    else runs.push({ s: w.startMs, e: w.endMs });
  }
  for (const r of runs) {
    points.push({ ms: Math.max(0, r.s - attack), db: opts.duckDb });
    points.push({ ms: r.e + release, db: 0 });
  }
  return points.sort((a, b) => a.ms - b.ms);
}

/** The part of a clip the pre-cut light preview depends on: the pieces in order, and how the voice is treated. Same on the server and in the editor. */
export function previewCutKey(clip: { segments: Segment[]; voiceDb: number; voiceMuted: boolean; voiceClean?: { enabled: boolean; amount: number; sampleStartMs: number | null; sampleEndMs: number | null; enhance?: boolean }; fixes?: { sharpen: boolean; stabilise: boolean; colour: boolean }; background?: { mode: string; amount: number; colour: string } }): string {
  const vc = clip.voiceClean;
  const fx = clip.fixes && (clip.fixes.sharpen || clip.fixes.stabilise || clip.fixes.colour) ? { x: [clip.fixes.sharpen ? 1 : 0, clip.fixes.stabilise ? 1 : 0, clip.fixes.colour ? 1 : 0] } : {};
  const bg = clip.background && clip.background.mode !== "room" ? { b: [clip.background.mode, Math.round(clip.background.amount * 100), clip.background.colour] } : {};
  return JSON.stringify({ s: clip.segments.map((x) => [Math.round(x.inMs), Math.round(x.outMs)]), v: clip.voiceDb, m: clip.voiceMuted, c: vc?.enabled ? [vc.amount, vc.sampleStartMs, vc.sampleEndMs] : null, ...(vc?.enhance ? { e: 1 } : {}), ...fx, ...bg });
}
/** Whether any one-click picture fix is on: the fixed footage then stands in for the media in the render and the pre-cut. */
export const fixesOn = (clip: { fixes?: { sharpen: boolean; stabilise: boolean; colour: boolean } }): boolean => !!clip.fixes && (clip.fixes.sharpen || clip.fixes.stabilise || clip.fixes.colour);
/** Whether the room behind the speaker is treated (which needs the speaker cutout). */
export const backgroundOn = (clip: { background?: { mode: string } }): boolean => !!clip.background && clip.background.mode !== "room";
/** Whether the speaker's track is processed at all (cleaned, polished, or both): the processed file then stands in for the media everywhere. */
export const voiceProcessed = (clip: { voiceClean?: { enabled: boolean; enhance?: boolean } }): boolean => !!clip.voiceClean && (clip.voiceClean.enabled || !!clip.voiceClean.enhance);
